import { Directive, KeyValueDiffers, KeyValueDiffer, AfterContentInit, OnDestroy, DOCUMENT, inject, output, contentChildren, OutputRefSubscription } from '@angular/core';
import { DraggableDirective } from './draggable.directive';

interface DragPosition {
  left: number;
  right: number;
  index: number;
  element: HTMLElement;
}

interface DragEvent {
  model: { prop: string; [key: string]: unknown };
  event: MouseEvent & { x?: number; y?: number };
  element?: HTMLElement;
}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[orderable]'
})
export class OrderableDirective implements AfterContentInit, OnDestroy {
  private readonly document = inject(DOCUMENT);

  readonly reorder = output<{ prevIndex: number; newIndex: number; model: unknown }>();
  readonly targetChanged = output<{ prevIndex?: number; newIndex?: number; initialIndex?: number }>();

  readonly draggables = contentChildren(DraggableDirective, { descendants: true });

  positions: Record<string, DragPosition>;
  differ: KeyValueDiffer<string, DraggableDirective>;
  lastDraggingIndex: number;
  private readonly subscriptions = new Map<DraggableDirective, Array<OutputRefSubscription>>();

  constructor() {
    const differs = inject(KeyValueDiffers);

    this.differ = differs.find({}).create();
  }

  ngAfterContentInit(): void {
    // HACK: Investigate Better Way
    this.updateSubscriptions();
    // Note: contentChildren() is a signal; we use effect() to reactively respond to changes
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subs: Array<OutputRefSubscription>) => subs.forEach((s: OutputRefSubscription) => s.unsubscribe()));
    this.subscriptions.clear();
  }

  updateSubscriptions(): void {
    const diffs = this.differ.diff(this.createMapDiffs());

    if (diffs) {
      const subscribe = ({ currentValue, previousValue }: { currentValue: DraggableDirective; previousValue: DraggableDirective }): void => {
        unsubscribe({ previousValue });

        if (currentValue) {
          const subs: Array<OutputRefSubscription> = [
            currentValue.dragStart.subscribe(this.onDragStart.bind(this)),
            currentValue.dragging.subscribe((event: unknown) => this.onDragging(event as DragEvent)),
            currentValue.dragEnd.subscribe((event: unknown) => this.onDragEnd(event as DragEvent))
          ];
          this.subscriptions.set(currentValue, subs);
        }
      };

      const unsubscribe = ({ previousValue }: { previousValue: DraggableDirective }): void => {
        if (previousValue) {
          const subs2 = this.subscriptions.get(previousValue) ?? [];
          subs2.forEach((s: OutputRefSubscription) => s.unsubscribe());
          this.subscriptions.delete(previousValue);
        }
      };

      diffs.forEachAddedItem(subscribe);
      // diffs.forEachChangedItem(subscribe.bind(this));
      diffs.forEachRemovedItem(unsubscribe);
    }
  }

  onDragStart(): void {
    this.positions = {};

    let i = 0;
    for (const dragger of this.draggables()) {
      const elm = dragger.element;
      const left = parseInt(elm.offsetLeft.toString(), 0);
      this.positions[(dragger.dragModel() as { prop: string }).prop] = {
        left,
        right: left + parseInt(elm.offsetWidth.toString(), 0),
        index: i++,
        element: elm
      };
    }
  }

  onDragging({ model, event }: DragEvent): void {
    const prevPos = this.positions[model.prop];
    const target = this.isTarget(model, event);

    if (target) {
      if (this.lastDraggingIndex !== target.i) {
        this.targetChanged.emit({
          prevIndex: this.lastDraggingIndex,
          newIndex: target.i,
          initialIndex: prevPos.index
        });
        this.lastDraggingIndex = target.i;
      }
    } else if (this.lastDraggingIndex !== prevPos.index) {
      this.targetChanged.emit({
        prevIndex: this.lastDraggingIndex,
        initialIndex: prevPos.index
      });
      this.lastDraggingIndex = prevPos.index;
    }
  }

  onDragEnd({ element, model, event }: DragEvent): void {
    const prevPos = this.positions[model.prop];

    const target = this.isTarget(model, event);
    if (target) {
      this.reorder.emit({
        prevIndex: prevPos.index,
        newIndex: target.i,
        model
      });
    }

    this.lastDraggingIndex = undefined;
    element.style.left = 'auto';
  }

  isTarget(model: { prop: string }, event: MouseEvent & { x?: number; y?: number }): { pos: DragPosition; i: number } | undefined {
    let i = 0;
    const x = event.x || event.clientX;
    const y = event.y || event.clientY;
    const targets = this.document.elementsFromPoint(x, y);

    for (const prop in this.positions) {
      if (!Object.prototype.hasOwnProperty.call(this.positions, prop)) { continue; }
      // current column position which throws event.
      const pos = this.positions[prop];

      // since we drag the inner span, we need to find it in the elements at the cursor
      if (model.prop !== prop && targets.find((el: Element) => el === pos.element)) {
        return {
          pos,
          i
        };
      }

      i++;
    }
  }

  private createMapDiffs(): Record<string, DraggableDirective> {
    return this.draggables().reduce((acc: Record<string, DraggableDirective>, curr: DraggableDirective) => {
      acc[(curr.dragModel() as { $$id: string }).$$id] = curr;
      return acc;
    }, {});
  }
}
